import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const produitsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Produits IS TELECOM',
  description: 'Gamme complète de produits certifiés pour projets de télécommunication et énergétiques : équipements radio, câbles fibre, armoires de stockage, batteries lithium, onduleurs hybrides.',
  url: 'https://is-telecom.co/produits',
  numberOfItems: 12,
  itemListElement: [
    { '@type': 'Product', position: 1, name: 'Unité Radio Huawei AAU5613', description: 'Unité radio actif 5G NR Band n78, 64T64R massive MIMO, 200W.' },
    { '@type': 'Product', position: 2, name: 'Câble Fibre Optique ADSS 48 Fibres', description: 'Câble fibre optique autoportant pour déploiement aérien, 48 fibres monomodes OS2.' },
    { '@type': 'Product', position: 3, name: 'Boitier de Jonction Fibre 24 Ports', description: 'Boîtier dépannage et épissurage IP68 pour réseaux FTTH et backbone.' },
    { '@type': 'Product', position: 4, name: 'Testeur OTDR Viavi MTS-5800', description: 'Testeur réflectométrique professionnel pour certifications de liens fibre.' },
    { '@type': 'Product', position: 5, name: 'Armoire de Stockage Modulaire 10kWh', description: 'Solution de stockage modulaire et sécurisée pour installations domestiques et petits commerces.' },
    { '@type': 'Product', position: 6, name: 'Armoire de Stockage Industrielle 50kWh', description: 'Système de stockage haute capacité pour applications industrielles et micro-réseaux.' },
    { '@type': 'Product', position: 7, name: 'Batterie Lithium 48V 200Ah', description: 'Batterie haute performance avec gestion intelligente BMS intégrée.' },
    { '@type': 'Product', position: 8, name: 'Batterie Lithium 24V 100Ah', description: 'Batterie compacte idéale pour systèmes résidentiels et petites installations.' },
    { '@type': 'Product', position: 9, name: 'Onduleur Hybride 5kW', description: 'Onduleur hors réseau et hybride haute efficacité pour installations domestiques.' },
    { '@type': 'Product', position: 10, name: 'Onduleur Hybride 10kW Triphasé', description: 'Onduleur triphasé haute puissance pour applications commerciales et industrielles.' },
    { '@type': 'Product', position: 11, name: 'Module de Commutation Intelligent', description: 'Système de contrôle et commutation automatique avec monitoring intégré.' },
    { '@type': 'Product', position: 12, name: 'Kit de Câblage Solaire Professionnel', description: 'Câbles, connecteurs et protections complets pour installation solaire.' },
  ],
};

const categories = [
  { id: 'tous', label: 'Tous les produits' },
  { id: 'telecom', label: 'Télécommunication' },
  { id: 'stockage', label: 'Stockage' },
  { id: 'batteries', label: 'Batteries' },
  { id: 'onduleurs', label: 'Onduleurs' },
  { id: 'modules', label: 'Modules' },
  { id: 'accessoires', label: 'Accessoires' },
];

const produits = [
  {
    id: 100,
    category: 'telecom',
    name: 'Unité Radio Huawei AAU5613',
    desc: 'Unité radio actif 5G NR Band n78, 64T64R massive MIMO, 200W, Idéal pour déploiements 5G urbains et péri-urbains.',
    specs: [
      { label: 'Technologie', value: '5G NR / LTE' },
      { label: 'Puissance', value: '200W' },
      { label: 'MIMO', value: '64T64R' },
      { label: 'Certification', value: 'Huawei Cert.' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Huawei%205G%20radio%20unit%20antenna%20module%20for%20telecom%20tower%2C%20modern%20active%20antenna%20unit%20AAU%20for%20cellular%20network%2C%20professional%20telecommunications%20equipment%20product%20photography%2C%20clean%20white%20background%2C%20detailed%20technical%20hardware&width=500&height=350&seq=prod-tel-aau-01&orientation=landscape',
  },
  {
    id: 101,
    category: 'telecom',
    name: 'Câble Fibre Optique ADSS 48 Fibres',
    desc: 'Câble fibre optique autoportant pour déploiement aérien, 48 fibres monomodes OS2, gaine UV, portée max 100m.',
    specs: [
      { label: 'Fibres', value: '48 x OS2' },
      { label: 'Standard', value: 'ITU-T G.652D' },
      { label: 'Portée max', value: '100 m' },
      { label: 'Température', value: '-40 à +70°C' },
    ],
    image: 'https://readdy.ai/api/search-image?query=ADSS%20fiber%20optic%20cable%20spool%20for%20aerial%20deployment%2C%20optical%20fiber%20cable%20with%20protective%20jacket%20on%20reel%2C%20telecommunications%20infrastructure%20product%20photography%2C%20clean%20background%20with%20cable%20detail%20showing%20cross-section&width=500&height=350&seq=prod-tel-fibre-01&orientation=landscape',
  },
  {
    id: 102,
    category: 'telecom',
    name: 'Boitier de Jonction Fibre 24 Ports',
    desc: 'Boîtier dépannage et épissurage pour réseaux FTTH et backbone. IP68, 24 ports SC/APC, compatible poteaux et chambres.',
    specs: [
      { label: 'Ports', value: '24 x SC/APC' },
      { label: 'Protection', value: 'IP68' },
      { label: 'Fibres max', value: '48' },
      { label: 'Installation', value: 'Aérien / Enterré' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Fiber%20optic%20splice%20closure%20junction%20box%20with%20organized%20cable%20management%2C%20IP68%20rated%20telecommunications%20enclosure%20with%20SC%20APC%20connectors%2C%20professional%20network%20infrastructure%20component%2C%20clean%20technical%20product%20photography&width=500&height=350&seq=prod-tel-box-01&orientation=landscape',
  },
  {
    id: 103,
    category: 'telecom',
    name: 'Testeur OTDR Viavi MTS-5800',
    desc: 'Testeur réflectométrique professionnel pour certifications de liens fibre, prise en charge multi-longueurs d’onde, rapport automatique PDF.',
    specs: [
      { label: 'Portée', value: '260 km' },
      { label: 'Précision', value: '±0.5 m' },
      { label: 'Longueurs', value: '1310/1550 nm' },
      { label: 'Batterie', value: '8h' },
    ],
    image: 'https://readdy.ai/api/search-image?query=OTDR%20optical%20time%20domain%20reflectometer%20testing%20device%20for%20fiber%20optic%20network%2C%20professional%20telecommunications%20measurement%20equipment%20with%20display%20screen%2C%20clean%20product%20photography%20on%20white%20background%2C%20technical%20measurement%20tool&width=500&height=350&seq=prod-tel-otdr-01&orientation=landscape',
  },
  {
    id: 1,
    category: 'stockage',
    name: 'Armoire de Stockage Modulaire 10kWh',
    desc: 'Solution de stockage modulaire et sécurisée pour installations domestiques et petits commerces.',
    specs: [
      { label: 'Capacité', value: '10 kWh' },
      { label: 'Tension', value: '48V DC' },
      { label: 'Cycles', value: '6000+' },
      { label: 'Garantie', value: '10 ans' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Modern%20solar%20energy%20storage%20cabinet%20with%20sleek%20white%20design%2C%2010kWh%20lithium%20battery%20system%2C%20professional%20industrial%20equipment%20with%20clean%20aesthetic%2C%20high-tech%20renewable%20energy%20storage%2C%20product%20photography%20on%20neutral%20gray%20background&width=500&height=350&seq=prod-storage-10-01&orientation=landscape',
  },
  {
    id: 2,
    category: 'stockage',
    name: 'Armoire de Stockage Industrielle 50kWh',
    desc: 'Système de stockage haute capacité pour applications industrielles et micro-réseaux.',
    specs: [
      { label: 'Capacité', value: '50 kWh' },
      { label: 'Tension', value: '384V DC' },
      { label: 'Cycles', value: '8000+' },
      { label: 'Garantie', value: '15 ans' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Large%20industrial%20battery%20energy%20storage%20system%2C%2050kWh%20capacity%20cabinet%20with%20metallic%20finish%2C%20professional%20renewable%20energy%20equipment%20for%20microgrid%2C%20heavy%20duty%20power%20storage%20unit%2C%20product%20photography%20on%20clean%20background&width=500&height=350&seq=prod-storage-50-01&orientation=landscape',
  },
  {
    id: 3,
    category: 'batteries',
    name: 'Batterie Lithium 48V 200Ah',
    desc: 'Batterie haute performance avec gestion intelligente BMS intégrée.',
    specs: [
      { label: 'Capacité', value: '9.6 kWh' },
      { label: 'Tension', value: '48V' },
      { label: 'Durée de vie', value: '15+ ans' },
      { label: 'Efficacité', value: '95%+' },
    ],
    image: 'https://readdy.ai/api/search-image?query=High%20performance%20lithium%20battery%20pack%2048V%20200Ah%2C%20modern%20industrial%20battery%20module%20with%20clean%20design%2C%20professional%20renewable%20energy%20storage%20component%2C%20lithium%20ion%20power%20unit%2C%20product%20photography%20on%20white%20background&width=500&height=350&seq=prod-batt-48v-01&orientation=landscape',
  },
  {
    id: 4,
    category: 'batteries',
    name: 'Batterie Lithium 24V 100Ah',
    desc: 'Batterie compacte idéale pour systèmes résidentiels et petites installations.',
    specs: [
      { label: 'Capacité', value: '2.4 kWh' },
      { label: 'Tension', value: '24V' },
      { label: 'Durée de vie', value: '12+ ans' },
      { label: 'Efficacité', value: '93%+' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Compact%20lithium%20battery%2024V%20100Ah%2C%20small%20residential%20energy%20storage%20battery%20module%2C%20clean%20modern%20design%20with%20white%20casing%2C%20professional%20power%20storage%20component%2C%20product%20photography%20on%20neutral%20background&width=500&height=350&seq=prod-batt-24v-01&orientation=landscape',
  },
  {
    id: 5,
    category: 'onduleurs',
    name: 'Onduleur Hybride 5kW',
    desc: 'Onduleur hors réseau et hybride haute efficacité pour installations domestiques.',
    specs: [
      { label: 'Puissance', value: '5 kW' },
      { label: 'Efficacité', value: '97.5%' },
      { label: 'MPPT', value: '2 entrées' },
      { label: 'Protection', value: 'IP65' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Modern%20hybrid%20solar%20inverter%205kW%2C%20sleek%20white%20electronic%20device%20for%20renewable%20energy%20conversion%2C%20professional%20power%20electronics%20with%20LCD%20display%2C%20clean%20product%20photography%20on%20gray%20background&width=500&height=350&seq=prod-inv-5kw-01&orientation=landscape',
  },
  {
    id: 6,
    category: 'onduleurs',
    name: 'Onduleur Hybride 10kW Triphasé',
    desc: 'Onduleur triphasé haute puissance pour applications commerciales et industrielles.',
    specs: [
      { label: 'Puissance', value: '10 kW' },
      { label: 'Efficacité', value: '98%' },
      { label: 'MPPT', value: '4 entrées' },
      { label: 'Protection', value: 'IP65' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Industrial%20three-phase%20hybrid%20solar%20inverter%2010kW%2C%20large%20professional%20power%20electronics%20unit%2C%20metallic%20cabinet%20with%20cooling%20system%2C%20renewable%20energy%20conversion%20equipment%2C%20product%20photography%20on%20clean%20background&width=500&height=350&seq=prod-inv-10kw-01&orientation=landscape',
  },
  {
    id: 7,
    category: 'modules',
    name: 'Module de Commutation Intelligent',
    desc: 'Système de contrôle et commutation automatique avec monitoring intégré.',
    specs: [
      { label: 'Courant max', value: '100A' },
      { label: 'Tension', value: '1000V DC' },
      { label: 'Communication', value: 'RS485, WiFi' },
      { label: 'Garantie', value: '3 ans' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Intelligent%20switching%20module%20for%20solar%20system%2C%20professional%20electronic%20control%20device%20with%20circuit%20board%20and%20connectors%20visible%2C%20modern%20power%20management%20component%2C%20clean%20technical%20product%20photography%20on%20white%20background&width=500&height=350&seq=prod-module-01&orientation=landscape',
  },
  {
    id: 8,
    category: 'accessoires',
    name: 'Kit de Câblage Solaire Professionnel',
    desc: 'Câbles, connecteurs et protections complets pour installation solaire.',
    specs: [
      { label: 'Section', value: '4mm² / 6mm²' },
      { label: 'Longueur', value: '100m' },
      { label: 'Connecteurs', value: 'MC4 étanches' },
      { label: 'Certification', value: 'TUV, UL' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Professional%20solar%20cable%20kit%20with%20MC4%20connectors%20neatly%20organized%2C%20high%20quality%20photovoltaic%20wiring%20accessories%2C%20cable%20management%20system%20with%20solar%20panel%20connectors%2C%20clean%20product%20photography%20on%20white%20background&width=500&height=350&seq=prod-cables-01&orientation=landscape',
  },
];

export default function Produits() {
  const [activeCategory, setActiveCategory] = useState('tous');

  const filtered = activeCategory === 'tous'
    ? produits
    : produits.filter((p) => p.category === activeCategory);

  return (
    <Layout>
      <JsonLd data={produitsSchema} />
      {/* Hero */}
      <section className="relative py-20 bg-background-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20product%20showcase%20background%20with%20geometric%20shapes%20and%20green%20gradient%20on%20dark%20navy%2C%20modern%20catalog%20design%20for%20renewable%20energy%20products%2C%20clean%20professional%20corporate%20aesthetic%20with%20subtle%20light%20accents&width=1920&height=700&seq=prod-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">
            Nos Produits
          </h1>
          <p className="text-lg md:text-xl text-background-300 max-w-3xl mx-auto">
            Équipements de télécommunication et systèmes énergétiques certifiés pour tous vos projets d&apos;infrastructure
          </p>
        </div>
      </section>

      {/* Filters */}
      <ScrollReveal variant="fade-up">
      <section className="py-8 bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  activeCategory === cat.id
                    ? 'bg-background-950 text-white border-background-950'
                    : 'bg-white text-foreground-600 border-background-200/70 hover:border-primary-300/60'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Products Grid */}
      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-16 bg-background-50" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-3xl overflow-hidden border border-background-200/70 hover:shadow-lg transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5">
                  <h3 className="font-bold font-heading text-foreground-950 text-base mb-2 leading-snug">{p.name}</h3>
                  <p className="text-foreground-500 text-sm mb-4 leading-relaxed">{p.desc}</p>
                  <div className="space-y-1.5 mb-5">
                    {p.specs.map((spec) => (
                      <div key={spec.label} className="flex justify-between text-sm">
                        <span className="text-foreground-400">{spec.label}</span>
                        <span className="font-medium text-foreground-950">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/devis" className="block w-full text-center bg-accent-50 hover:bg-accent-100 text-accent-700 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer">
                    Demander un devis
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-4xl text-foreground-300 mb-4 w-10 h-10 flex items-center justify-center mx-auto"></i>
              <h3 className="text-xl font-medium text-foreground-950 mb-2">Aucun produit dans cette catégorie</h3>
            </div>
          )}
        </div>
      </section>
      </ScrollReveal>

      {/* Quality */}
      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Qualité</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
              Qualité et Certification
            </h2>
            <p className="text-lg text-foreground-500 max-w-3xl mx-auto">
              Tous nos produits sont testés et certifiés selon les normes internationales les plus strictes
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'CE', desc: 'Conformité européenne' },
              { icon: 'ri-flashlight-line', title: 'TUV', desc: 'Sécurité électrique' },
              { icon: 'ri-battery-charge-line', title: 'UN38.3', desc: 'Transport batteries' },
              { icon: 'ri-earth-line', title: 'IEC', desc: 'Standards internationaux' },
            ].map((cert) => (
              <div key={cert.title} className="bg-white rounded-3xl p-6 text-center border border-background-200/70 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${cert.icon} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                </div>
                <div className="font-bold font-heading text-foreground-950 text-lg">{cert.title}</div>
                <div className="text-foreground-500 text-sm">{cert.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">Besoin d&apos;un catalogue complet ?</h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            Contactez-nous pour recevoir notre catalogue produits détaillé avec toutes les spécifications techniques.
          </p>
          <a href="/contact" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
            Demander le catalogue
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </section>
      </ScrollReveal>
    </Layout>
  );
}